import { motion, AnimatePresence } from 'framer-motion';
import type { ComponentProps } from 'react';
import { useState, type ReactNode } from 'react';
import { cn } from './helpers';

type AnimatedTransitionProps = {
  isPending: boolean;
  isError?: boolean;
  isFound?: boolean;
  animateOnEveryRender?: boolean;
  children: ReactNode;
  skeleton: ReactNode;
  duration?: number;
  mode?: 'wait' | 'sync' | 'popLayout';
  error?: ReactNode | null;
  notFound?: ReactNode | null;
  withAnimation?: boolean;
} & ComponentProps<typeof motion.div>;

const Content = ({
  duration,
  isPending,
  isError,
  isFound,
  withAnimation,
  skeleton,
  error,
  notFound,
  children,
  className,
  ...props
}: Omit<AnimatedTransitionProps, 'mode' | 'animateOnEveryRender'>) => {
  const animationProps = withAnimation
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration },
      }
    : {};

  let key = 'content';
  let content = children;

  if (isPending) {
    key = 'loading';
    content = skeleton;
  } else if (isError) {
    key = 'error';
    content = error;
  } else if (isFound === false) {
    key = 'not-found';
    content = notFound;
  }

  return (
    <motion.div
      key={key}
      className={cn('w-full h-full', className)}
      {...animationProps}
      {...props}
    >
      {content}
    </motion.div>
  );
};

export function AnimatedTransition({
  duration = 0.1,
  mode = 'wait',
  isPending,
  isError,
  isFound,
  animateOnEveryRender = false,
  skeleton,
  error,
  notFound,
  children,
  className,
  ...props
}: AnimatedTransitionProps) {
  const [isRenderedBefore] = useState(!isPending);

  const contentProps = {
    withAnimation: true,
    isError,
    isFound,
    isPending,
    skeleton,
    error,
    notFound,
    children,
    className,
    duration,
    ...props,
  };

  if (isRenderedBefore && !animateOnEveryRender) {
    return (
      <Content
        {...contentProps}
        withAnimation={false}
      />
    );
  }

  return (
    <AnimatePresence mode={mode}>
      <Content {...contentProps} />
    </AnimatePresence>
  );
}
